import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import {
  type GamesFilter,
  GamesFilterSortDirection,
  GamesFilterSortType,
  type GameV1,
} from '@/api/games';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { fetcherSSV2 } from '@/modules/api/fetcher';
import { useGetCommonStore } from '@/store/commonStore';

interface IUseGamesInfiniteParams extends GamesFilter.Body {
  provider?: string;
  category?: string;
}

export const useGamesInfinite = ({
  provider,
  category,
  device,
  currencies = ['FUN'],
  pageSize,
  sort,
}: IUseGamesInfiniteParams) => {
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const getKey = useCallback(
    (pageIndex: number, previousPageData: GamesFilter.Response | null) => {
      if (previousPageData && !previousPageData.pagination.nextPage) {
        return null;
      }

      const page = pageIndex + 1;

      return [
        'games_filter',
        provider,
        category,
        device,
        currencies,
        pageSize,
        page,
        isAuthenticated,
      ] as const;
    },
    [provider, category, device, currencies, pageSize, isAuthenticated]
  );

  const fetcher = (
    key: readonly [
      string,
      string | undefined,
      string | undefined,
      string,
      string[],
      number,
      number,
      boolean,
    ]
  ): Promise<GamesFilter.Response> => {
    const [
      ,
      keyProvider,
      keyCategory,
      keyDevice,
      keyCurrencies,
      keyPageSize,
      keyPage,
      // isAuthenticated is part of the key for cache invalidation but not used in fetcher
    ] = key;

    const filter: Record<string, unknown> = {};
    if (keyProvider) filter.providers = [keyProvider];
    if (keyCategory) filter.categories = { identifiers: [keyCategory] };

    return fetcherSSV2<GamesFilter.Response>('/games_filter', {
      method: 'POST',
      body: JSON.stringify({
        device: keyDevice,
        currencies: keyCurrencies,
        filter,
        ...(keyProvider && {
          sort: {
            type: GamesFilterSortType.POPULARITY,
            direction: GamesFilterSortDirection.ASC,
          },
        }),
        page_size: keyPageSize,
        page: keyPage,
        sort,
      }),
    });
  };

  const { data, size, setSize, isLoading, isValidating } =
    useSWRInfinite<GamesFilter.Response>(getKey, fetcher, {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      persistSize: false,
    });

  const games: GameV1[] = useMemo(
    () => (data ? data.flatMap((page) => mapGameV2ToGameV1(page.data)) : []),
    [data]
  );

  const lastPage = data?.at(-1);
  const hasMoreGames = Boolean(lastPage?.pagination.nextPage);

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');

  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMoreGames) {
      setSize((prevSize) => prevSize + 1);
    }
  }, [isLoadingMore, hasMoreGames, setSize]);

  return {
    games,
    hasMoreGames,
    isLoading,
    isLoadingMore,
    isValidating,
    loadMore,
  };
};
